import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import featureService from '../services/featureService';

/**
 * Module-level cache of the current user's enabled feature keys, so multiple
 * components calling useFeature don't each hit the API. Keyed by user id; an
 * in-flight request is de-duplicated via a shared promise.
 */
let cache = { userId: null, keys: null };
let inFlight = null;

/** Drop the cache — call after login/logout so the next read refetches. */
export const resetFeatureCache = () => {
  cache = { userId: null, keys: null };
  inFlight = null;
};

function loadEnabledFeatures(userId) {
  if (cache.userId === userId && cache.keys) {
    return Promise.resolve(cache.keys);
  }
  if (!inFlight) {
    inFlight = featureService
      .getEnabledFeatures()
      .then((keys) => {
        cache = { userId, keys: keys || [] };
        return cache.keys;
      })
      .finally(() => { inFlight = null; });
  }
  return inFlight;
}

/**
 * Whether a feature is enabled for the current user.
 *
 * @param {string} key feature key, e.g. "creator_intelligence"
 * @returns {{ enabled: boolean, loading: boolean }}
 *
 * Fail-closed: while loading, or on any error, `enabled` is false — the UI stays
 * hidden rather than flashing a feature the backend hasn't granted.
 */
export const useFeature = (key) => {
  const { user } = useSelector((s) => s.auth);
  const userId = user?.id || null;
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    if (!userId) {
      setEnabled(false);
      setLoading(false);
      return () => {};
    }
    setLoading(true);
    loadEnabledFeatures(userId)
      .then((keys) => { if (active) setEnabled(Array.isArray(keys) && keys.includes(key)); })
      .catch(() => { if (active) setEnabled(false); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [key, userId]);

  return { enabled, loading };
};

export default useFeature;
